import { useState, FormEvent, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import CategoryPickerModal from "./CategoryPickerModal";

// ─── Password helpers ──────────────────────────────────────────────────────────
function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return "At least 8 characters required";
  if (!/[0-9]/.test(pwd)) return "Must contain at least 1 number";
  if (!/[^a-zA-Z0-9]/.test(pwd)) return "Must contain at least 1 special character";
  return null;
}

function getStrength(pwd: string): { score: number; label: string; color: string } {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  if (pwd.length >= 12) score++;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "bg-red-500", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];
  return { score, label: labels[score], color: colors[score] };
}

// ─── OTP Boxes ─────────────────────────────────────────────────────────────────
function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { isDark } = useTheme();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (i: number, v: string) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const arr = value.split("").concat(Array(6).fill("")).slice(0, 6);
    arr[i] = digit;
    const joined = arr.join("");
    onChange(joined);
    if (digit && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const arr = value.split("").concat(Array(6).fill("")).slice(0, 6);
      if (arr[i]) { arr[i] = ""; onChange(arr.join("")); }
      else if (i > 0) { arr[i - 1] = ""; onChange(arr.join("")); inputs.current[i - 1]?.focus(); }
    }
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted.padEnd(6, "").slice(0, 6));
    e.preventDefault();
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex gap-2.5 justify-center my-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className={`w-12 h-13 text-center rounded-xl border font-syne font-bold text-xl outline-none transition-all duration-150 focus:ring-2 ${
            isDark
              ? "bg-[#1a1a1a] border-white/10 text-white focus:ring-white/20 focus:border-white/30"
              : "bg-[#f5f5f5] border-black/10 text-[#111] focus:ring-black/10 focus:border-black/25"
          }`}
          style={{ height: "52px" }}
        />
      ))}
    </div>
  );
}

// ─── Main Modal ────────────────────────────────────────────────────────────────
type Step = "signup" | "verify";

interface EarlyAccessSignupModalProps {
  onClose: () => void;
}

export default function EarlyAccessSignupModal({ onClose }: EarlyAccessSignupModalProps) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { signUp, verifySignupOtp, resendOtp } = useAuth();

  const [step, setStep] = useState<Step>("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const strength = getStrength(password);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const startCooldown = () => {
    if (cooldownRef.current) return;
    setResendCooldown(15);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); cooldownRef.current = null; return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const pwErr = validatePassword(password);
    if (pwErr) { setError(pwErr); return; }
    if (!username.trim()) { setError("Please choose a username."); return; }
    setLoading(true);
    const { error: err } = await signUp(email, password);
    if (err) {
      setError(err.message);
    } else {
      setOtp("");
      setStep("verify");
      startCooldown();
    }
    setLoading(false);
  };

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.replace(/\D/g, "").length < 6) { setError("Enter the full 6-digit code."); return; }
    setError(null);
    setLoading(true);
    const { error: err } = await verifySignupOtp(email, otp.replace(/\D/g, ""));
    if (err) {
      setError("Invalid or expired code. Try requesting a new one.");
      setLoading(false);
      return;
    }
    // Save display_name after successful verification
    if (username.trim()) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("profiles").update({ display_name: username.trim() }).eq("id", user.id);
      }
    }
    setLoading(false);
    setShowCategoryPicker(true);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError(null);
    const { error: err } = await resendOtp(email, "signup");
    setResendLoading(false);
    if (err) { setError(err.message || "Failed to resend. Try again shortly."); return; }
    startCooldown();
  };

  // ─── Shared styles ────────────────────────────────────────────────────────
  const inputClass = `w-full border rounded-xl px-4 py-3.5 font-inter text-sm focus:outline-none focus:ring-2 transition-all duration-150 ${
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-[#444] focus:ring-white/15 focus:border-white/25"
      : "bg-[#fafafa] border-black/10 text-[#111] placeholder:text-[#aaa] focus:ring-black/8 focus:border-black/25"
  }`;

  const submitBtnClass = `w-full font-inter font-semibold text-sm py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
    isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
  }`;

  return (
    <>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ animation: "eaFadeIn 0.2s ease" }}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md cursor-pointer" onClick={onClose} />

        {/* Card */}
        <div
          className={`relative z-10 w-full max-w-[440px] rounded-3xl overflow-hidden border transition-colors duration-300 ${
            isDark ? "bg-[#0a0a0a] border-white/10" : "bg-white border-black/8"
          }`}
          style={{ animation: "eaSlideUp 0.28s cubic-bezier(0.34,1.56,0.64,1)" }}
        >
          {/* Top strip */}
          <div className={`h-1 w-full ${isDark ? "bg-gradient-to-r from-white/20 via-white/40 to-white/10" : "bg-gradient-to-r from-black/10 via-black/20 to-black/5"}`} />

          {/* Header */}
          <div className="px-8 pt-8 pb-0 flex items-start justify-between">
            <div>
              <p className={`font-syne font-bold text-xl tracking-tight ${isDark ? "text-white" : "text-[#111]"}`}>
                {step === "signup" ? "Create your account" : "Verify your email"}
              </p>
              <p className={`font-inter text-sm mt-1 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                {step === "signup"
                  ? "Start tracking your flips for free"
                  : `We sent a code to ${email}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 cursor-pointer transition-colors ${
                isDark ? "text-[#555] hover:text-white hover:bg-white/8" : "text-[#bbb] hover:text-[#111] hover:bg-black/6"
              }`}
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Body */}
          <div className="px-8 pt-6 pb-8">
            {step === "signup" ? (
              <form onSubmit={handleSignUp} className="flex flex-col gap-3.5">
                {/* Username */}
                <div className="relative">
                  <div className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center ${isDark ? "text-[#444]" : "text-[#ccc]"}`}>
                    <i className="ri-user-line text-sm" />
                  </div>
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    maxLength={32}
                    className={`${inputClass} pl-10`}
                    autoFocus
                  />
                </div>

                {/* Email */}
                <div className="relative">
                  <div className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center ${isDark ? "text-[#444]" : "text-[#ccc]"}`}>
                    <i className="ri-mail-line text-sm" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className={`${inputClass} pl-10`}
                  />
                </div>

                {/* Password */}
                <div className={`flex items-center gap-2 px-4 py-3.5 rounded-xl border transition-all duration-150 focus-within:ring-2 ${
                  isDark
                    ? "bg-white/5 border-white/10 focus-within:ring-white/15 focus-within:border-white/25"
                    : "bg-[#fafafa] border-black/10 focus-within:ring-black/8 focus-within:border-black/25"
                }`}>
                  <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${isDark ? "text-[#444]" : "text-[#ccc]"}`}>
                    <i className="ri-lock-line text-sm" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    className={`flex-1 bg-transparent outline-none font-inter text-sm ${isDark ? "text-white placeholder:text-[#444]" : "text-[#111] placeholder:text-[#aaa]"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className={`w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0 ${isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"}`}
                  >
                    <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
                  </button>
                </div>

                {/* Password strength */}
                {password.length > 0 && (
                  <div className="space-y-1.5 px-1">
                    <div className="flex items-center gap-2">
                      <div className={`flex-1 h-1 rounded-full overflow-hidden ${isDark ? "bg-white/8" : "bg-black/8"}`}>
                        <div className={`h-full rounded-full transition-all duration-300 ${strength.color}`} style={{ width: `${(strength.score / 4) * 100}%` }} />
                      </div>
                      {strength.label && (
                        <span className={`font-inter text-[10px] ${strength.score <= 1 ? "text-red-400" : strength.score === 2 ? "text-amber-400" : "text-emerald-400"}`}>
                          {strength.label}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                      {[
                        { ok: password.length >= 8, label: "8+ characters" },
                        { ok: /[0-9]/.test(password), label: "1 number" },
                        { ok: /[^a-zA-Z0-9]/.test(password), label: "1 special char" },
                      ].map((req) => (
                        <span key={req.label} className={`font-inter text-[10px] flex items-center gap-1 ${req.ok ? "text-emerald-400" : isDark ? "text-[#555]" : "text-[#bbb]"}`}>
                          <i className={`${req.ok ? "ri-checkbox-circle-line" : "ri-circle-line"} text-[10px]`} />
                          {req.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {error && <p className="font-inter text-xs text-red-400 px-1">{error}</p>}

                <button type="submit" disabled={loading} className={`${submitBtnClass} mt-1`}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      Creating account…
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Get Early Access
                      <i className="ri-arrow-right-line text-sm" />
                    </span>
                  )}
                </button>

                {/* Divider */}
                <div className={`flex items-center gap-3 my-1 ${isDark ? "text-[#333]" : "text-[#e0e0e0]"}`}>
                  <div className={`flex-1 h-px ${isDark ? "bg-white/6" : "bg-black/6"}`} />
                  <span className={`font-inter text-xs ${isDark ? "text-[#444]" : "text-[#ccc]"}`}>Already have an account?</span>
                  <div className={`flex-1 h-px ${isDark ? "bg-white/6" : "bg-black/6"}`} />
                </div>

                <a
                  href="/dashboard"
                  className={`w-full font-inter text-sm text-center py-3 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    isDark ? "border-white/10 text-[#888] hover:text-white hover:border-white/25" : "border-black/10 text-[#888] hover:text-[#111] hover:border-black/25"
                  }`}
                >
                  Sign in instead
                </a>

                {/* Trust signals */}
                <div className="flex items-center justify-center gap-4 mt-2">
                  {["Free to start", "No credit card", "800+ resellers"].map((t) => (
                    <span key={t} className={`font-inter text-[10px] flex items-center gap-1 ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
                      <i className="ri-check-line text-[10px]" />
                      {t}
                    </span>
                  ))}
                </div>
              </form>
            ) : (
              /* ─── Verify Step ─────────────────────────────────────────────── */
              <div>
                <div className="flex flex-col items-center mb-2">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isDark ? "bg-white/5 border border-white/8" : "bg-black/4 border border-black/6"}`}>
                    <i className={`ri-mail-check-line text-2xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
                  </div>
                  <p className={`font-inter text-sm text-center leading-relaxed ${isDark ? "text-[#666]" : "text-[#888]"}`}>
                    Enter the 6-digit code we sent to<br />
                    <strong className={isDark ? "text-white/80" : "text-[#333]"}>{email}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerify} className="flex flex-col gap-3">
                  <OtpInput value={otp} onChange={setOtp} />
                  {error && <p className="font-inter text-xs text-red-400 text-center">{error}</p>}
                  <button type="submit" disabled={loading || otp.replace(/\D/g, "").length < 6} className={`${submitBtnClass} mt-1`}>
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-sm" />
                        Verifying…
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Confirm &amp; Continue
                        <i className="ri-arrow-right-line text-sm" />
                      </span>
                    )}
                  </button>
                </form>

                {/* Resend */}
                <div className={`mt-5 rounded-2xl px-5 py-4 flex items-center justify-between ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-black/[0.025] border border-black/[0.06]"}`}>
                  <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't get it?"}
                  </p>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0 || resendLoading}
                    className={`font-inter text-xs font-medium cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDark ? "text-white/60 hover:text-white" : "text-[#555] hover:text-[#111]"
                    }`}
                  >
                    {resendLoading ? "Sending…" : "Resend code"}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setStep("signup"); setError(null); setOtp(""); }}
                  className={`mt-4 font-inter text-xs flex items-center gap-1 cursor-pointer transition-colors ${isDark ? "text-[#444] hover:text-[#777]" : "text-[#ccc] hover:text-[#888]"}`}
                >
                  <i className="ri-arrow-left-line text-xs" />
                  Back
                </button>
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes eaFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes eaSlideUp {
            from { opacity: 0; transform: translateY(32px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>

      {showCategoryPicker && (
        <CategoryPickerModal
          onComplete={() => { onClose(); navigate("/dashboard"); }}
          onSkip={() => { onClose(); navigate("/dashboard"); }}
        />
      )}
    </>
  );
}
